/**
 * Decodes a JWT's payload for UI convenience only (which nav items to show, when to preemptively
 * refresh). This NEVER validates the signature and MUST NEVER be used to make an authorization
 * decision — the server (via the gateway, ADR-0010) is always the enforcement point
 * (docs/development/FrontendGuidelines.md §6.4).
 */

/** Mirrors the claim shape documented in ADR-0006. */
export interface JwtClaims {
  sub: string;
  email: string;
  roles: string[];
  vendor_id?: string;
  company_id?: string;
  jti: string;
  iat: number;
  exp: number;
}

export function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/** True once the token's `exp` claim is in the past (or the token is unparsable). */
export function isJwtExpired(token: string): boolean {
  const claims = decodeJwt(token);
  if (!claims) return true;
  return claims.exp * 1000 <= Date.now();
}

/** Milliseconds until expiry, clamped to 0. Used to schedule a proactive silent refresh. */
export function msUntilExpiry(token: string): number {
  const claims = decodeJwt(token);
  if (!claims) return 0;
  return Math.max(0, claims.exp * 1000 - Date.now());
}
